import { useEffect, useState, type ReactNode } from 'react'
import '@/features/f0/styles/bodyFonts'
import { useReferenceStyles } from './useReferenceStyles'
import { ProtoButton } from '@/features/d0/states'
import { anchorProps } from '@/features/f0/tutorial/anchors'
import { useAuthStore } from '@/stores/authStore'
import { useConsentStore } from '@/stores/consentStore'
import api from '@/services/api'
import { DEV_MODE } from '@/lib/devMode'
import AIDisclosureOverlay from './AIDisclosureOverlay'
import '../u6.css'

export interface ConsentOverlayProps {
  onAccept: () => void
  loading?: boolean
  error?: string | null
}

// Mandatory ToS re-acceptance overlay; Escape does not dismiss it.
export function ConsentOverlay({ onAccept, loading, error }: ConsentOverlayProps) {
  useReferenceStyles()
  return (
    <div id="zupa-complete" className="zupa-app z-ov" role="dialog" aria-modal="true" aria-labelledby="consent-tos-title">
      <section className="s7-panel z-ov__card">
        <h2 id="consent-tos-title">
          Updated Terms of Service
        </h2>
        <p>
          We&apos;ve updated our <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>{' '}
          and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. Please review
          and accept them to continue using Zupa.
        </p>
        {error && <p className="z-error" role="alert">{error}</p>}
        <div className="z-ov__actions" {...anchorProps('consent-choice')}>
          <ProtoButton className="z-primary" autoFocus onClick={onAccept} disabled={loading}>
            {loading ? 'Saving...' : 'I Accept'}
          </ProtoButton>
        </div>
      </section>
    </div>
  )
}

interface ConsentGateProps {
  children: ReactNode
}

// Ported from legacy ConsentGate: ToS first, then AI disclosure, then children.
export default function ConsentGate({ children }: ConsentGateProps) {
  const { isAuthenticated } = useAuthStore()
  const [tosAccepted, setTosAccepted] = useState(true) // optimistic default
  const [aiDisclosureAccepted, setAiDisclosureAccepted] = useState(true) // optimistic default
  const [isAccepting, setIsAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)
  const setConsent = useConsentStore((s) => s.setConsent)

  useEffect(() => {
    if (DEV_MODE || !isAuthenticated) {
      setChecked(true)
      setConsent({ tosAccepted: true, aiDisclosureAccepted: true, resolved: true })
      return
    }

    api.consentStatus()
      .then(({ tos_accepted, ai_disclosure_accepted }) => {
        setTosAccepted(tos_accepted)
        setAiDisclosureAccepted(ai_disclosure_accepted)
        setConsent({ tosAccepted: tos_accepted, aiDisclosureAccepted: ai_disclosure_accepted, resolved: true })
      })
      .catch(() => {
        // If the check fails, don't block the user
        setTosAccepted(true)
        setAiDisclosureAccepted(true)
        setConsent({ tosAccepted: true, aiDisclosureAccepted: true, resolved: true })
      })
      .finally(() => {
        setChecked(true)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const handleAccept = async () => {
    setIsAccepting(true)
    setError(null)
    try {
      await api.acceptTerms()
      setTosAccepted(true)
      setConsent({ tosAccepted: true })
    } catch {
      setError('Failed to record your acceptance. Please try again.')
    } finally {
      setIsAccepting(false)
    }
  }

  // Don't render anything until we've checked (avoid flash)
  if (!checked) return null

  // The app stays unmounted while a consent is pending: a dialog it opened (the first-visit
  // workspace guide) would aria-hide the mandatory overlay from assistive tech.
  if (!tosAccepted) {
    return <ConsentOverlay onAccept={handleAccept} loading={isAccepting} error={error} />
  }

  if (!aiDisclosureAccepted) {
    return (
      <AIDisclosureOverlay
        onAccepted={() => {
          setAiDisclosureAccepted(true)
          setConsent({ aiDisclosureAccepted: true })
        }}
      />
    )
  }

  return <>{children}</>
}
